//Built-in libraries

import { MapPinIcon } from '@heroicons/react/24/solid';
import { useQueries } from '@tanstack/react-query';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useEffect, useMemo, useRef, useState } from 'react';
//Third party libraries
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { getAddressDecode } from '@/api/misc';
import type { IGetProjectsServiceResData } from '@/api/Sherpa/Project/modules/ProjectCore/types';
import TableFooter from '@/components/Table/TableFooter';
import GoogleMap from '@/components/UI/GoogleMap';
import { useProjectsData } from '@/hooks/ReactQueryHooks/useProjectQuery';
//Services
import { usePermission } from '@/hooks/usePermission';
import { workspaceSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { FiltersInterface } from '@/types/Miscellaneous';
//Assets andd Miscellaneous
import type { ListProjectType } from '@/types/Project';
import type ProjectDetail from '@/types/ProjectwithGeo';
import { isObjectEmpty } from '@/utils';
import { toGeoCoordinate } from '@/utils/geoCoordinates';
import MapViewProjectCard from './MapViewProjectCard';

const DEFAULT_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_LIST_ITEMS_PER_PAGE = 3;
const LIST_PER_PAGE_OPTIONS = [3, 6, 9, 12];
const MAP_ID = 'sherpa-projects-map';

function buildProjectAddress(project: IGetProjectsServiceResData & { address?: string; city?: string; state?: string; zipCode?: string }) {
  if (project.fullAddress?.trim()) {
    return project.fullAddress.trim();
  }

  return [project.address, project.city, project.state, project.zipCode].filter(Boolean).join(', ').trim();
}

//Component Begin
export const MapViewProjectsList = ({ filters }: { filters: FiltersInterface }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const { currentWorkspace } = useSelector(workspaceSelector);
  const [selectedProjectId, setSelectedProjectId] = useState(0);
  const [selectedMarker, setSelectedMarker] = useState<number>(0);
  const projectLocationHandler = (data: ProjectDetail) => {
    setSelectedProjectId(data.id);
  };

  const [perPage, setPerPage] = useState<number>(DEFAULT_LIST_ITEMS_PER_PAGE);
  const [page, setPage] = useState<number>(1);

  const fetchProjects = useProjectsData({
    params: { perPage: 9999, page: 1, ...filters },
  });
  const allProjects = fetchProjects?.data?.data ?? [];
  const totalProjects = fetchProjects?.data?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(Number(totalProjects) / perPage));
  const showingCount = Math.min(page * perPage, totalProjects);
  const projects = useMemo(() => {
    const start = (page - 1) * perPage;
    return allProjects.slice(start, start + perPage);
  }, [allProjects, page, perPage]);

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setPage((prevState) => (prevState > 1 ? prevState - 1 : 1));
    } else if (direction === 'next') {
      setPage((prevState) => (prevState >= totalPages ? totalPages : prevState + 1));
    }
  };

  const refs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    refs.current = [];
  }, [page, projects]);

  useEffect(() => {
    const selectedProjectIndex = projects?.findIndex((project: ListProjectType) => project.id === selectedProjectId);
    if (selectedProjectIndex != null && selectedProjectIndex >= 0 && refs.current[selectedProjectIndex]) {
      refs.current[selectedProjectIndex]?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [selectedProjectId, projects]);

  const projectDetailViewHandler = (projectId: number) => {
    if (hasPermission(['READ_PROJECT'])) {
      navigate(`${location.pathname}/${projectId}`, { state: location });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'You do not have permission to read project details',
          toastType: 'info',
        })
      );
    }
  };

  const fetchProjectGeoCode = useQueries({
    queries: allProjects.map((project: IGetProjectsServiceResData) => {
      const address = buildProjectAddress(project);

      return {
        queryKey: ['GET_PROJECT_GEO_CODE', project.id, address],
        enabled: address.length >= 5,
        retry: 1,
        queryFn: async (): Promise<ProjectDetail | null> => {
          try {
            const response = await getAddressDecode(address);
            const coordinates = toGeoCoordinate(response?.data?.lat, response?.data?.lng);

            if (!coordinates) {
              return null;
            }

            return {
              ...project,
              coordinates,
            };
          } catch {
            return null;
          }
        },
      };
    }),
  });

  const geocodedProjects = useMemo(
    () => fetchProjectGeoCode.map((query) => query.data).filter((project): project is ProjectDetail => project != null),
    [fetchProjectGeoCode]
  );

  const mapCenterRef = useRef(DEFAULT_MAP_CENTER);
  if (geocodedProjects.length > 0 && mapCenterRef.current === DEFAULT_MAP_CENTER) {
    mapCenterRef.current = geocodedProjects[0].coordinates;
  }

  const isInitialLoading = fetchProjects.isLoading && allProjects.length === 0;

  return (
    <>
      {!isInitialLoading && totalProjects > 0 && (
        <>
          <section>
            <div className='w-full lg:h-screen/70 flex flex-col xl:flex-row justify-between bg-white'>
              <div className='w-full h-screen/3 mt-14 xl:h-full overflow-hidden'>
                <GoogleMap defaultCenter={mapCenterRef.current} mapId={MAP_ID}>
                  {geocodedProjects.map((project) => (
                    <AdvancedMarker key={project.id} position={project.coordinates}>
                      <div
                        onClick={() => {
                          setSelectedMarker(project.id);
                          projectLocationHandler(project);
                        }}
                        className='relative'
                      >
                        <MapPinIcon className={selectedMarker === project.id ? 'h-8 w-8 text-black' : 'h-8 w-8 text-red-500 '} />
                      </div>
                    </AdvancedMarker>
                  ))}
                </GoogleMap>
              </div>
              <div className='pl-4 w-full xl:w-2/3 flex flex-col min-h-0 h-full'>
                <div className='pr-4 my-5 shrink-0'>
                  <p>
                    Showing {showingCount} of {totalProjects} Projects
                  </p>
                </div>
                <ul className='w-full flex-1 overflow-y-auto gap-4 flex flex-col items-start'>
                  {projects.map((project: IGetProjectsServiceResData, idx: number) => (
                    <MapViewProjectCard
                      key={project.id}
                      ref={(element) => {
                        refs.current[idx] = element;
                      }}
                      project={project}
                      selectedProjectId={selectedProjectId}
                      onClick={() => projectDetailViewHandler(project.id)}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </section>
          <TableFooter
            currentPage={page}
            totalPage={totalPages}
            perPage={perPage}
            perPageOptions={LIST_PER_PAGE_OPTIONS}
            perPageHandler={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            pageChangeHandler={onPageChangeHandler}
          />
        </>
      )}
      {!isInitialLoading && !isObjectEmpty(filters) && totalProjects === 0 && (
        <section>
          <div className='w-full flex justify-center items-center h-screen/2 bg-white p-4 text-center'>
            <div>
              <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                <MapPinIcon className='h-8 w-8' />
                <h1>{totalProjects} Projects</h1>
              </div>
              <p>
                There are {totalProjects} projects matching your criteria in{' '}
                {currentWorkspace ? <span className='text-primaryColor-normal'>{currentWorkspace?.name}</span> : 'all'} program.
              </p>
            </div>
          </div>
        </section>
      )}
    </>
  );
};
